import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    ApartmentOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    DeploymentUnitOutlined,
    DownOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    RadarChartOutlined,
    RobotOutlined,
    SearchOutlined,
    SendOutlined,
    SyncOutlined,
    ToolOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getAnalysisTrace, getDocument, getEntity, getOverview, prefetchDocument, searchDocuments, triggerSync } from "./marketIntelligenceApi";
import "./market-intelligence.css";

const EMPTY_SEARCH = {
    items: [],
    pagination: { page: 1, page_size: 100, total: 0 },
    available_filters: { routes: [], airlines: [], aircraft: [], sources: [] },
};
const VALID_DOC_TYPES = new Set(["home", "daily_brief", "quote", "route", "airline", "source", "aircraft", "market_update", "news", "report", "other"]);
const VALID_ANALYSIS_STATUSES = new Set(["pending", "recognizing", "completed", "failed", "unavailable"]);
const VALID_SORTS = new Set(["source_modified_desc", "date_desc", "title_asc"]);
const ENTITY_TYPES = new Set(["route", "airline", "source", "aircraft"]);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TYPE_LABEL = {
    daily_brief: "每日简报",
    quote: "报价",
    route: "航线",
    airline: "航司",
    source: "信源",
    aircraft: "机型",
    market_update: "市场观察",
    news: "新闻",
    report: "报告",
    other: "其他",
    home: "首页",
};
export const MARKET_INTELLIGENCE_QUOTE_ARCHIVE_PATH = "/market-intelligence?view=quotes";
let marketIntelligenceListScrollTop = 0;
let shouldRestoreMarketIntelligenceScroll = false;

function marketIntelligenceScrollContainer() {
    return window.document.querySelector(".app-shell__content");
}

function rememberMarketIntelligenceScroll() {
    const container = marketIntelligenceScrollContainer();
    marketIntelligenceListScrollTop = container?.scrollTop || 0;
    shouldRestoreMarketIntelligenceScroll = true;
}

function resetMarketIntelligenceScroll() {
    const container = marketIntelligenceScrollContainer();
    if (container?.scrollTo) container.scrollTo({ top: 0, left: 0, behavior: "auto" });
    else if (container) container.scrollTop = 0;
}

function documentHref(id) {
    return `/market-intelligence/document?id=${encodeURIComponent(id)}`;
}

function documentLinkProps(document) {
    return {
        to: documentHref(document.id),
        state: { marketIntelligencePreview: document },
        viewTransition: true,
        onClick: rememberMarketIntelligenceScroll,
        onMouseEnter: () => void prefetchDocument(document.id),
        onFocus: () => void prefetchDocument(document.id),
    };
}

function prefetchDocumentHref(href) {
    const id = new URLSearchParams(href.split("?")[1] || "").get("id");
    if (id) void prefetchDocument(id);
}

function errorMessage(cause, fallback) {
    return cause?.response?.data?.detail || cause?.response?.data?.message || cause?.message || fallback;
}

function fromUrl(params) {
    const raw = (key) => params.get(key) || undefined;
    const documentType = raw("document_type");
    const analysisStatus = raw("analysis_status");
    const sort = raw("sort");
    const fuelRaw = raw("fuel_included");
    const pageRaw = Number(raw("page") ?? 1);
    const dateFrom = raw("date_from");
    const dateTo = raw("date_to");
    return {
        q: raw("q"),
        document_type: documentType && VALID_DOC_TYPES.has(documentType) ? documentType : undefined,
        date_from: dateFrom && ISO_DATE_RE.test(dateFrom) ? dateFrom : undefined,
        date_to: dateTo && ISO_DATE_RE.test(dateTo) ? dateTo : undefined,
        route: raw("route"),
        airline: raw("airline"),
        aircraft: raw("aircraft"),
        source: raw("source"),
        fuel_included: fuelRaw ? fuelRaw === "true" : undefined,
        analysis_status: analysisStatus && VALID_ANALYSIS_STATUSES.has(analysisStatus) ? analysisStatus : undefined,
        sort: sort && VALID_SORTS.has(sort) ? sort : undefined,
        page: Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1,
        page_size: 100,
    };
}

function toUrl(params) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "" && !(key === "page" && value === 1) && key !== "page_size") query.set(key, String(value));
    });
    return query.toString();
}

// ---- Wiki Link Utilities ----

export function wikiLinkHref(link) {
    if (!link.resolved || !link.target_document_id) return null;
    if (link.target_document_type && ENTITY_TYPES.has(link.target_document_type) && link.target_entity_key) {
        return `/market-intelligence/entity?type=${encodeURIComponent(link.target_document_type)}&key=${encodeURIComponent(link.target_entity_key)}`;
    }
    return documentHref(link.target_document_id);
}

/**
 * 预处理 markdown：将 [[wiki/path|label]] 替换为标准 markdown 链接，
 * 这样 react-markdown 可以原生渲染它们。
 */
function preprocessWikiLinks(markdown, wikiLinks = []) {
    if (!markdown) return markdown;
    return markdown.replace(/\[\[([^\]]+)\]\]/g, (match, inner) => {
        const parts = inner.split("|");
        const target = parts[0];
        const label = parts[1] || target.split("/").at(-1) || target;
        const resolved = wikiLinks.find(
            (item) => item.target_path?.replace(/\.md$/i, "") === target.replace(/\.md$/i, "") || item.label === label,
        );
        const href = resolved ? wikiLinkHref(resolved) : null;
        if (href) return `[${label}](${href})`;
        return `**${label}**`;
    });
}

function stripFrontmatter(md) {
    return md.replace(/^---\n[\s\S]*?\n---\n?/, "");
}

// ---- Markdown Renderer (react-markdown based) ----

function createMarkdownComponents(wikiLinks) {
    return {
        a({ href, children }) {
            if (href?.startsWith("/market-intelligence")) {
                return (
                    <Link
                        className="mi-inline-link"
                        to={href}
                        viewTransition
                        onMouseEnter={() => prefetchDocumentHref(href)}
                        onFocus={() => prefetchDocumentHref(href)}
                    >
                        {children}
                    </Link>
                );
            }
            if (/^(https?:\/\/|mailto:)/i.test(href || "")) {
                return <a className="mi-inline-link" href={href} rel="noreferrer noopener" target="_blank">{children}</a>;
            }
            return <span>{children}</span>;
        },
        table({ children }) {
            return <div className="mi-table-wrap"><table>{children}</table></div>;
        },
    };
}

export function MarkdownReader({ markdown, wikiLinks = [] }) {
    const processed = useMemo(
        () => preprocessWikiLinks(stripFrontmatter(markdown || ""), wikiLinks),
        [markdown, wikiLinks],
    );
    const components = useMemo(() => createMarkdownComponents(wikiLinks), [wikiLinks]);

    if (!processed?.trim()) return null;

    return (
        <article className="mi-markdown">
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={components}
            >
                {processed}
            </ReactMarkdown>
        </article>
    );
}

// ---- Sub Components ----

function FuelBadge({ value }) {
    const state = value === true ? "included" : value === false ? "excluded" : "unknown";
    return <span className={`mi-fuel ${state}`}>{value === true ? "含油" : value === false ? "+ 燃油" : "油费待确认"}</span>;
}

function analysisLabel(status) {
    if (status === "completed") return "AI 已分析";
    if (status === "pending" || status === "recognizing") return "AI 分析中";
    return "AI 暂不可用";
}

function documentLead(document, fields) {
    if (document.document_type === "quote") {
        return [
            fields.route ? `${fields.route} 航线报价` : "航线报价",
            fields.source ? `信源 ${fields.source}` : null,
            fields.date ? `业务日期 ${fields.date}` : null,
        ].filter(Boolean).join(" · ");
    }
    const typeLabel = TYPE_LABEL[document.document_type] || "市场情报";
    return [
        `${typeLabel}资料`,
        fields.route ? `关联航线 ${fields.route}` : null,
        fields.source ? `信源 ${fields.source}` : null,
        fields.date ? `业务日期 ${fields.date}` : null,
    ].filter(Boolean).join(" · ");
}

function DocumentResults({ result, onClear }) {
    if (!result.items.length) {
        return (
            <div className="mi-panel mi-empty">
                <FileSearchOutlined />
                <div>
                    <h3>没有找到符合条件的情报</h3>
                    <p>可以减少筛选条件或清除查询。</p>
                    <button type="button" onClick={onClear}>清除筛选</button>
                </div>
            </div>
        );
    }
    return (
        <div className="mi-result-list">
            {result.items.map((item) => (
                <Link key={item.id} {...documentLinkProps(item)}>
                    <div className="mi-result-meta">
                        <span>{TYPE_LABEL[item.document_type] || item.document_type}</span>
                        <time>{item.deterministic_fields.date || new Date(item.source_modified_at).toLocaleDateString("zh-CN")}</time>
                        {item.document_type === "quote" && <FuelBadge value={item.deterministic_fields.fuel_included} />}
                        {item.document_type !== "quote" && item.relationship?.source === "ai_candidate" && (
                            <span className={`mi-confidence mi-confidence-${item.relationship.confidence}`}>
                                AI 候选关联 · {item.relationship.confidence}
                            </span>
                        )}
                        {item.document_type !== "quote" && (
                            <span className={`mi-analysis-state ${item.analysis_status}`}>{analysisLabel(item.analysis_status)}</span>
                        )}
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.document_type === "quote" ? "打开查看结构化报价与原始文档。" : item.summary || "暂无摘要，可打开查看原文。"}</p>
                </Link>
            ))}
        </div>
    );
}

function hasPrice(quote) {
    const prices = quote.deterministic_fields.prices;
    return prices && prices.length > 0 && prices.some((p) =>
        p.amount !== null && p.amount !== undefined
        || p.min_amount !== null && p.min_amount !== undefined && p.max_amount !== null && p.max_amount !== undefined
    );
}

function priceText(quote) {
    const prices = quote.deterministic_fields.prices || [];
    const unit = quote.deterministic_fields.price_unit ? `/${quote.deterministic_fields.price_unit}` : "";
    const values = prices.filter((p) =>
        p.amount !== null && p.amount !== undefined
        || p.min_amount !== null && p.min_amount !== undefined && p.max_amount !== null && p.max_amount !== undefined
    ).map((p) => {
        const amount = p.amount !== null && p.amount !== undefined
            ? new Intl.NumberFormat("en-US").format(p.amount)
            : `${new Intl.NumberFormat("en-US").format(p.min_amount)}–${new Intl.NumberFormat("en-US").format(p.max_amount)}`;
        return `${amount} ${p.currency}${unit}`;
    }).join(" / ");
    return values || "金额待补充";
}

function QuoteRows({ quotes, requirePrice = false }) {
    const visible = (requirePrice ? quotes.filter(hasPrice) : [...quotes]).sort((a, b) => {
        const da = a.deterministic_fields.date || "";
        const db = b.deterministic_fields.date || "";
        return db.localeCompare(da);
    });
    if (!visible.length) return null;

    return (
        <div className="mi-quote-list">
            <div className="mi-quote-head" aria-hidden="true">
                <span>航线</span>
                <span>最新报价</span>
                <span>价格口径</span>
                <span>承运信息</span>
                <span>业务日期</span>
            </div>
            {visible.map((quote) => {
                const f = quote.deterministic_fields;
                return (
                    <Link className="mi-quote-row" key={quote.id} {...documentLinkProps(quote)}>
                        <span className="mi-quote-row-route">{f.route || quote.title}</span>
                        <span className="mi-quote-row-price">{priceText(quote)}</span>
                        <span className="mi-quote-row-basis"><FuelBadge value={f.fuel_included} /></span>
                        <span className="mi-quote-row-carrier">
                            {f.airline && <span className="mi-quote-row-tag">{f.airline}</span>}
                            {f.aircraft && <span className="mi-quote-row-tag">{f.aircraft}</span>}
                            {!f.airline && !f.aircraft && <span className="mi-quote-row-empty">待确认</span>}
                        </span>
                        <time className="mi-quote-row-date">{f.date || "日期待补充"}</time>
                    </Link>
                );
            })}
        </div>
    );
}

function LatestQuotes({ quotes }) {
    if (!quotes.some(hasPrice)) return null;
    const capped = quotes.filter(hasPrice).slice(0, 6);
    return (
        <section className="mi-panel mi-quotes-section" id="latest-quotes">
            <div className="mi-section-title">
                <div>
                    <span className="mi-eyebrow">航线价格台</span>
                    <h2>最新航线报价</h2>
                    <p>按业务日期排序，快速核对价格口径、燃油与承运信息。</p>
                </div>
                <Link to={MARKET_INTELLIGENCE_QUOTE_ARCHIVE_PATH}>查看全部 <ArrowRightOutlined /></Link>
            </div>
            <QuoteRows quotes={capped} requirePrice />
        </section>
    );
}

function SyncIndicator({ status, canSync, syncing, onSync }) {
    const stateLabel = { healthy: "数据可用", syncing: "同步中", warning: "有同步警告", error: "同步异常，展示上次成功内容" };
    const run = status.last_run;
    const lastRunSucceeded = run && ["success", "success_with_warnings"].includes(run.status);
    const triggerLabel = { manual: "手动", scheduled: "自动", startup: "启动" }[run?.trigger];
    const lastSuccessLabel = !status.last_success_at
        ? "尚无成功同步"
        : status.state === "syncing" || status.state === "error" || !lastRunSucceeded
            ? `上次成功同步于 ${new Date(status.last_success_at).toLocaleString("zh-CN")}`
            : `上次数据同步${triggerLabel ? `（${triggerLabel}）` : ""}于 ${new Date(status.last_success_at).toLocaleString("zh-CN")}`;
    const checkerKnown = typeof status.scheduler_running === "boolean";
    const checkStatusLabel = {
        never: "等待首次检查",
        checking: "正在检查",
        unchanged: "源数据无变化",
        change_detected: "发现变化，等待稳定",
        settling: "等待文件写入完成",
        syncing: "发现变化，正在同步",
        synced: "变化已同步",
        error: "检查失败",
    }[status.last_check_status] || "状态未知";
    let checkerLabel = null;
    if (checkerKnown) {
        if (status.scheduler_running) {
            const checkedAt = status.last_checked_at
                ? `最近检查 ${new Date(status.last_checked_at).toLocaleTimeString("zh-CN")}`
                : "尚未执行首次检查";
            checkerLabel = `自动检查运行中 · ${checkedAt} · ${checkStatusLabel}`;
        } else if (!status.scheduler_enabled) {
            checkerLabel = "自动检查未开启";
        } else if (!status.webdav_configured) {
            checkerLabel = "自动检查未启动 · 数据源配置不完整";
        } else {
            checkerLabel = "自动检查未启动";
        }
    }
    return (
        <div className="mi-sync">
            <div className="mi-sync-status">
                <span className={`mi-sync-dot ${status.state}`} />
                <div>
                    <strong>{stateLabel[status.state] || "等待首次同步"}</strong>
                    <small>{lastSuccessLabel}</small>
                    {checkerLabel && <small className={status.scheduler_running ? "mi-check-running" : "mi-check-stopped"}>{checkerLabel}</small>}
                </div>
            </div>
            {canSync && (
                <div className="mi-sync-actions">
                    <button type="button" disabled={syncing || status.running} onClick={onSync}>
                        <SyncOutlined spin={syncing || status.running} />立即同步
                    </button>
                </div>
            )}
        </div>
    );
}

function IntelligenceSearch({ value, onChange, onSubmit, onClear, loading }) {
    const hasQuery = Boolean(value.q);
    return (
        <div className="mi-search">
            <form onSubmit={(e) => { e.preventDefault(); onSubmit(value); }}>
                <SearchOutlined />
                <input
                    aria-label="搜索市场情报"
                    placeholder="搜索航线、航司、报价…"
                    value={value.q || ""}
                    onChange={(e) => onChange({ ...value, q: e.target.value || undefined, page: 1 })}
                />
                {loading && hasQuery && <span className="mi-search-loading">搜索中</span>}
                {hasQuery && <button className="mi-clear" type="button" onClick={onClear}><CloseOutlined /></button>}
            </form>
        </div>
    );
}

const CATEGORY_ORDER = ["daily_brief", "quote", "route", "airline", "source", "aircraft", "market_update", "news"];
const CATEGORY_ICON = {
    daily_brief: <CalendarOutlined />,
    quote: <SendOutlined />,
    route: <SendOutlined />,
    airline: <ApartmentOutlined />,
    source: <DeploymentUnitOutlined />,
    aircraft: <RobotOutlined />,
    market_update: <RadarChartOutlined />,
    news: <FileTextOutlined />,
};

function DocumentsByCategory({ items, onCategoryClick }) {
    const grouped = useMemo(() => {
        const map = {};
        items.forEach((item) => {
            const type = item.document_type || "other";
            if (!map[type]) map[type] = [];
            map[type].push(item);
        });
        return map;
    }, [items]);

    const categories = CATEGORY_ORDER.filter((t) => grouped[t]?.length);

    if (!categories.length) return null;

    return (
        <div className="mi-wiki-grid">
            {categories.map((type) => (
                <div className="mi-wiki-category" key={type}>
                    <button className="mi-wiki-category-head" type="button" onClick={() => onCategoryClick(type)}>
                        {CATEGORY_ICON[type]}
                        <span>{TYPE_LABEL[type]}</span>
                        <small>{grouped[type].length}</small>
                    </button>
                    <ul>
                        {grouped[type].slice(0, 5).map((item) => (
                            <li key={item.id}>
                                <Link {...documentLinkProps(item)}>
                                    {item.title}
                                </Link>
                            </li>
                        ))}
                        {grouped[type].length > 5 && (
                            <li className="mi-wiki-more">
                                <button type="button" onClick={() => onCategoryClick(type)}>
                                    还有 {grouped[type].length - 5} 条...
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function AnalysisJobTrace({ jobId }) {
    const [trace, setTrace] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    async function toggle() {
        const next = !open;
        setOpen(next);
        if (next && !trace) {
            setLoading(true);
            try { setTrace(await getAnalysisTrace(jobId)); }
            catch (cause) { message.error(errorMessage(cause, "诊断信息读取失败")); }
            finally { setLoading(false); }
        }
    }

    return (
        <section className="mi-trace">
            <button type="button" onClick={toggle}>
                <ToolOutlined />分析诊断轨迹<DownOutlined className={open ? "open" : ""} />
            </button>
            {open && (
                <div>
                    {loading ? <p>读取诊断信息...</p> : trace?.items?.length ? (
                        trace.items.map((item, index) => (
                            <article key={`${item.iteration}-${index}`}>
                                <strong>{item.iteration + 1}. {item.tool_name}</strong>
                                <pre>{JSON.stringify({ arguments: item.arguments, result: item.result_summary, error: item.error }, null, 2)}</pre>
                            </article>
                        ))
                    ) : <p>没有工具调用轨迹。</p>}
                </div>
            )}
        </section>
    );
}

function Skeleton() {
    return <div className="mi-panel mi-skeleton"><span /><span /><span /></div>;
}

function SourceBodySkeleton() {
    return (
        <div className="mi-source-skeleton" aria-label="正在读取文档正文">
            <span className="mi-skeleton-heading" />
            <span />
            <span />
            <span className="short" />
            <span className="mi-skeleton-table" />
        </div>
    );
}

function DocumentDetailSkeleton() {
    return (
        <main className="mi-page mi-detail">
            <div className="mi-content mi-detail-frame" aria-busy="true">
                <Link className="mi-back" to="/market-intelligence" viewTransition><ArrowLeftOutlined />返回市场情报</Link>
                <div className="mi-detail-head mi-detail-head-skeleton">
                    <div className="mi-detail-skeleton-copy">
                        <span className="mi-skeleton-chip" />
                        <span className="mi-skeleton-title" />
                        <span className="mi-skeleton-line" />
                    </div>
                    <span className="mi-skeleton-time" />
                </div>
                <div className="mi-fact-strip mi-fact-strip-skeleton">
                    {Array.from({ length: 4 }, (_, index) => <div key={index}><span /><strong /></div>)}
                </div>
                <section className="mi-analysis-notice mi-analysis-notice-skeleton">
                    <span className="mi-analysis-notice-icon" />
                    <div><strong /><p /></div>
                </section>
                <section className="mi-panel mi-source-panel">
                    <div className="mi-section-head mi-source-panel-head">
                        <div>
                            <span className="mi-eyebrow">原始依据</span>
                            <h2>文档正文</h2>
                            <p>正在读取知识库内容...</p>
                        </div>
                        <FileTextOutlined />
                    </div>
                    <div className="mi-source-body"><SourceBodySkeleton /></div>
                </section>
            </div>
        </main>
    );
}

// ============================================================
// Page Exports
// ============================================================

export function MarketIntelligencePage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, activeOrganization, hasAnyPermission } = useAuth();
    const quoteArchive = useMemo(() => new URLSearchParams(location.search).get("view") === "quotes", [location.search]);
    const queryFromUrl = useMemo(() => {
        const query = fromUrl(new URLSearchParams(location.search));
        return new URLSearchParams(location.search).get("view") === "quotes"
            ? { ...query, document_type: "quote" }
            : query;
    }, [location.search]);
    const [filters, setFilters] = useState(queryFromUrl);
    const [applied, setApplied] = useState(queryFromUrl);
    const [overview, setOverview] = useState(null);
    const [result, setResult] = useState(EMPTY_SEARCH);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const searchTimerRef = useRef(null);
    const canSync = Boolean(user?.is_superuser) || hasAnyPermission(["market_intelligence:sync"]);

    useLayoutEffect(() => {
        if (!shouldRestoreMarketIntelligenceScroll || loading || (!overview && !result.items.length)) return undefined;
        const frame = window.requestAnimationFrame(() => {
            const container = marketIntelligenceScrollContainer();
            if (container) container.scrollTo({ top: marketIntelligenceListScrollTop, left: 0, behavior: "auto" });
            shouldRestoreMarketIntelligenceScroll = false;
        });
        return () => window.cancelAnimationFrame(frame);
    }, [loading, overview, result.items.length]);

    useEffect(() => {
        setFilters(queryFromUrl);
        setApplied(queryFromUrl);
        setResult(EMPTY_SEARCH);
    }, [queryFromUrl]);

    const load = useCallback(async (query, { force = false } = {}) => {
        setLoading(true);
        setError(null);
        try {
            if (quoteArchive) {
                setResult(await searchDocuments(query, { force }));
            } else {
                const [overviewData, searchData] = await Promise.all([
                    getOverview({ force }),
                    searchDocuments(query, { force }),
                ]);
                setOverview(overviewData);
                setResult(searchData);
            }
        } catch (cause) {
            setError(errorMessage(cause, "市场情报暂时无法加载"));
        } finally {
            setLoading(false);
        }
    }, [quoteArchive]);

    useEffect(() => {
        const timer = window.setTimeout(() => void load(applied), 0);
        return () => window.clearTimeout(timer);
    }, [activeOrganization?.id, applied, load]);

    useEffect(() => () => {
        if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    }, []);

    const automaticChecksRunning = Boolean(overview?.sync_status?.scheduler_running);
    const lastSuccessAt = overview?.sync_status?.last_success_at || null;
    useEffect(() => {
        if (quoteArchive || (!overview?.sync_status?.running && !automaticChecksRunning)) return undefined;
        const interval = overview?.sync_status?.running ? 3000 : 30000;
        let cancelled = false;
        let timer;

        async function pollOverview() {
            try {
                const overviewData = await getOverview({ force: true });
                if (cancelled) return;
                setOverview(overviewData);
                if ((overviewData.sync_status?.last_success_at || null) !== lastSuccessAt) {
                    const searchData = await searchDocuments(applied, { force: true });
                    if (!cancelled) setResult(searchData);
                }
            } catch (cause) {
                if (!cancelled) setError(errorMessage(cause, "市场情报状态刷新失败"));
            } finally {
                if (!cancelled) timer = window.setTimeout(() => void pollOverview(), interval);
            }
        }

        timer = window.setTimeout(() => void pollOverview(), interval);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [applied, quoteArchive, overview?.sync_status?.running, automaticChecksRunning, lastSuccessAt]);

    function apply(next = filters) {
        const scoped = quoteArchive ? { ...next, document_type: "quote" } : next;
        setFilters(scoped);
        setApplied(scoped);
        const query = toUrl(scoped);
        const view = quoteArchive ? "view=quotes" : "";
        const search = [view, query].filter(Boolean).join("&");
        navigate(search ? `/market-intelligence?${search}` : "/market-intelligence", { replace: true });
    }

    function cancelScheduledSearch() {
        if (!searchTimerRef.current) return;
        window.clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
    }

    function changeSearch(next) {
        setFilters(next);
        cancelScheduledSearch();
        searchTimerRef.current = window.setTimeout(() => {
            searchTimerRef.current = null;
            apply({ ...next, q: next.q?.trim() || undefined, page: 1 });
        }, 300);
    }

    function submitSearch(next = filters) {
        cancelScheduledSearch();
        apply({ ...next, q: next.q?.trim() || undefined, page: 1 });
    }

    function clear() {
        cancelScheduledSearch();
        apply(quoteArchive
            ? { document_type: "quote", page: 1, page_size: 100 }
            : { page: 1, page_size: 100 });
    }

    async function syncNow() {
        setSyncing(true);
        try {
            const task = await triggerSync();
            message.success(task.already_running ? "已有同步任务正在运行" : "同步任务已启动");
            window.setTimeout(() => void load(applied, { force: true }), 1500);
        } catch (cause) {
            message.error(errorMessage(cause, "同步启动失败"));
        } finally {
            setSyncing(false);
        }
    }

    if (error && (quoteArchive ? !result.items.length : !overview)) {
        return (
            <main className="mi-page">
                <div className="mi-error">
                    <h1>市场情报暂时无法加载</h1>
                    <p>{error}</p>
                    <button type="button" onClick={() => { const clean = { page: 1, page_size: 100 }; apply(clean); void load(clean, { force: true }); }}>清空条件重试</button>
                </div>
            </main>
        );
    }

    const pagination = result.pagination;
    const goPage = (page) => apply({ ...filters, page });
    const hasNextPage = pagination.page * pagination.page_size < pagination.total;

    if (quoteArchive) {
        return (
            <main className="mi-page">
                <header className="mi-archive-hero">
                    <div className="mi-archive-hero-inner">
                        <Link className="mi-back" to="/market-intelligence"><ArrowLeftOutlined />返回市场情报</Link>
                        <span className="mi-eyebrow">Quote Archive</span>
                        <h1>全部航线报价</h1>
                        <p>按业务日期汇总已发布报价，点击任一航线查看完整口径与原始文档。</p>
                    </div>
                </header>
                <div className="mi-content mi-quote-archive">
                    <div className="mi-archive-toolbar">
                        <IntelligenceSearch
                            value={filters}
                            onChange={changeSearch}
                            onSubmit={submitSearch}
                            onClear={clear}
                            loading={loading}
                        />
                        <span>共 {pagination.total} 条报价</span>
                    </div>
                    {loading && !result.items.length ? <Skeleton /> : <QuoteRows quotes={result.items} requirePrice />}
                    {!loading && !result.items.length ? (
                        <div className="mi-panel mi-empty"><FileSearchOutlined /><div><h3>没有找到报价</h3><p>可以清除关键词后重试。</p></div></div>
                    ) : null}
                    {pagination.total > pagination.page_size && (
                        <div className="mi-pagination">
                            <button disabled={pagination.page <= 1} onClick={() => goPage(Math.max(1, pagination.page - 1))}>上一页</button>
                            <span>第 {pagination.page} 页</span>
                            <button disabled={!hasNextPage} onClick={() => goPage(pagination.page + 1)}>下一页</button>
                        </div>
                    )}
                </div>
            </main>
        );
    }

    return (
        <main className="mi-page">
            <header className="mi-hero">
                <div className="mi-hero-inner">
                    <div className="mi-hero-copy">
                        <span className="mi-eyebrow">FlyMeta Intelligence Desk</span>
                        <h1>市场情报</h1>
                        <p>汇总最新航线报价与关联资料，快速核对价格口径和原始依据。</p>
                    </div>
                    {overview && (
                        <SyncIndicator
                            status={overview.sync_status}
                            canSync={canSync}
                            syncing={syncing}
                            onSync={syncNow}
                        />
                    )}
                </div>
            </header>

            <div className="mi-content mi-home-content">
                {overview ? (
                    <div className="mi-home-stack">
                        <LatestQuotes quotes={overview.latest_quotes} />
                        <section className="mi-library-section" id="intelligence-library">
                            <div className="mi-library-head">
                                <div>
                                    <span className="mi-eyebrow">情报资料库</span>
                                    <h2>查找原始资料与关联档案</h2>
                                    <p>搜索航线、航司、报价和信源，快速回到原始资料核验。</p>
                                </div>
                                <span>{pagination.total} 篇资料</span>
                            </div>

                            <IntelligenceSearch
                                value={filters}
                                onChange={changeSearch}
                                onSubmit={submitSearch}
                                onClear={clear}
                                loading={loading}
                            />

                            {applied.q || applied.document_type ? (
                                <>
                                    <div className="mi-active-filter">
                                        <span>
                                            {applied.q ? `搜索: ${applied.q}` : `分类: ${TYPE_LABEL[applied.document_type] || applied.document_type}`}
                                        </span>
                                        <span>共 {pagination.total} 条</span>
                                        <button type="button" onClick={clear}><CloseOutlined />返回全部</button>
                                    </div>
                                    {loading && !result.items.length ? <Skeleton /> : <DocumentResults result={result} onClear={clear} />}
                                    {pagination.total > pagination.page_size && (
                                        <div className="mi-pagination">
                                            <button disabled={pagination.page <= 1} onClick={() => goPage(Math.max(1, pagination.page - 1))}>上一页</button>
                                            <span>第 {pagination.page} 页</span>
                                            <button disabled={!hasNextPage} onClick={() => goPage(pagination.page + 1)}>下一页</button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="mi-results-head">
                                        <h2>按业务主题浏览</h2>
                                        <span>共 {pagination.total} 篇文档</span>
                                    </div>
                                    {loading ? <Skeleton /> : <DocumentsByCategory items={result.items} onCategoryClick={(type) => apply({ ...filters, document_type: type, page: 1 })} />}
                                </>
                            )}
                        </section>
                    </div>
                ) : <Skeleton />}
            </div>
        </main>
    );
}

export function MarketIntelligenceDocumentPage() {
    const [params] = useSearchParams();
    const documentId = params.get("id")?.trim();
    const location = useLocation();
    const { user, activeOrganization, hasAnyPermission } = useAuth();
    const initialPreview = location.state?.marketIntelligencePreview;
    const [document, setDocument] = useState(() => initialPreview?.id === documentId ? initialPreview : null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const canDiagnose = Boolean(user?.is_superuser) || hasAnyPermission(["market_intelligence:sync"]);

    useLayoutEffect(() => {
        resetMarketIntelligenceScroll();
    }, [documentId]);

    useEffect(() => {
        if (!documentId) return undefined;
        let active = true;
        const routePreview = location.state?.marketIntelligencePreview;
        setDocument((current) => {
            if (current?.id === documentId && typeof current.markdown === "string") return current;
            return routePreview?.id === documentId ? routePreview : null;
        });
        setError(null);
        setLoading(true);
        getDocument(documentId)
            .then((value) => { if (active) setDocument(value); })
            .catch((cause) => { if (active) setError(errorMessage(cause, "文档读取失败")); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [activeOrganization?.id, documentId, location.state]);

    if (!documentId) {
        return (
            <main className="mi-page">
                <div className="mi-error">
                    <h1>缺少文档标识</h1>
                    <p>请从市场情报列表重新打开文档。</p>
                    <Link to="/market-intelligence">返回市场情报</Link>
                </div>
            </main>
        );
    }

    if (error && !document) {
        return (
            <main className="mi-page">
                <div className="mi-error">
                    <h1>文档无法打开</h1>
                    <p>{error}</p>
                    <Link to="/market-intelligence">返回市场情报</Link>
                </div>
            </main>
        );
    }

    if (!document) return <DocumentDetailSkeleton />;

    const fields = document.deterministic_fields || {};
    const analysis = document.ai_analysis || { status: document.analysis_status };
    const showAnalysis = document.document_type !== "quote";
    const lead = documentLead(document, fields);
    const sourceLoading = loading && typeof document.markdown !== "string";
    const factEntries = Object.entries({
        日期: fields.date,
        航线: fields.route,
        航司: fields.airline,
        机型: fields.aircraft,
        信源: fields.source,
    }).filter(([, value]) => value);

    return (
        <main className="mi-page mi-detail">
            <div className="mi-content mi-detail-frame" aria-busy={loading}>
                <Link className="mi-back" to="/market-intelligence" viewTransition><ArrowLeftOutlined />返回市场情报</Link>

                <header className="mi-detail-head">
                    <div className="mi-detail-head-main">
                        <div className="mi-result-meta">
                            <span>{TYPE_LABEL[document.document_type] || document.document_type}</span>
                            {showAnalysis && <span className={`mi-analysis-state ${document.analysis_status}`}>{analysisLabel(document.analysis_status)}</span>}
                            {document.document_type === "quote" && <FuelBadge value={fields.fuel_included} />}
                        </div>
                        <h1>{document.title}</h1>
                        <p>{lead}</p>
                    </div>
                    <div className="mi-source-time">
                        <ClockCircleOutlined />
                        <span>来源更新时间<strong>{new Date(document.source_modified_at).toLocaleString("zh-CN", { hour12: false })}</strong></span>
                    </div>
                </header>

                <section className="mi-fact-strip" aria-label="文档关键字段">
                    {factEntries.map(([label, value]) => (
                        <div key={label}><small>{label}</small><strong>{String(value)}</strong></div>
                    ))}
                    {fields.prices?.map((price) => (
                        <div className="mi-fact-price" key={`${price.currency}-${price.amount}`}>
                            <small>报价 · {price.currency}</small>
                            <strong>{new Intl.NumberFormat("en-US").format(price.amount)}</strong>
                        </div>
                    ))}
                </section>

                <section className="mi-panel mi-source-panel">
                    <div className="mi-section-head mi-source-panel-head">
                        <div>
                            <span className="mi-eyebrow">原始依据</span>
                            <h2>文档正文</h2>
                            <p>以下内容直接来自知识库，保留原始结构与可追溯链接。</p>
                        </div>
                        <FileTextOutlined />
                    </div>
                    <div className="mi-source-body">
                        {sourceLoading ? <SourceBodySkeleton /> : <MarkdownReader markdown={document.markdown || ""} wikiLinks={document.wiki_links} />}
                        {error && <div className="mi-source-load-error">正文更新失败，当前展示的是已加载信息。</div>}
                    </div>
                </section>

                {showAnalysis && (analysis.status === "completed" ? (
                    <section className="mi-panel mi-analysis mi-ai-generated">
                        <div className="mi-ai-banner"><RobotOutlined />AI 生成</div>
                        <div className="mi-section-head">
                            <div>
                                <span className="mi-eyebrow">读取 Agent</span>
                                <h2>管理摘要</h2>
                            </div>
                            <RobotOutlined />
                        </div>
                        <p className="mi-analysis-summary">{analysis.executive_summary}</p>
                        {analysis.key_points?.length > 0 && (
                            <ul>{analysis.key_points.map((item) => <li key={item}>{item}</li>)}</ul>
                        )}
                    </section>
                ) : (
                    <section className={`mi-analysis-notice ${document.analysis_status}`}>
                        <span className="mi-analysis-notice-icon"><RobotOutlined /></span>
                        <div>
                            <strong>{document.analysis_status === "pending" || document.analysis_status === "recognizing" ? "AI 正在整理这份资料" : "当前仅展示已确认内容"}</strong>
                            <p>
                                {document.analysis_status === "pending" || document.analysis_status === "recognizing"
                                    ? "分析完成后会在这里补充管理摘要、关键要点和待确认事项；原始文档不受影响。"
                                    : "AI 分析暂不可用，你仍可核对结构化字段和下方 Markdown 原文。"}
                            </p>
                        </div>
                    </section>
                ))}

                {document.wiki_links?.length > 0 && (
                    <section className="mi-related-section">
                        <div className="mi-section-title">
                            <div>
                                <span className="mi-eyebrow">知识关联</span>
                                <h2>关联阅读</h2>
                            </div>
                            <span>{document.wiki_links.length} 项</span>
                        </div>
                        <div className="mi-related">
                            {document.wiki_links.map((link, index) => {
                                const href = wikiLinkHref(link);
                                return href
                                    ? (
                                        <Link
                                            key={index}
                                            to={href}
                                            viewTransition
                                            onMouseEnter={() => prefetchDocumentHref(href)}
                                            onFocus={() => prefetchDocumentHref(href)}
                                        >
                                            {link.label}<small>{link.target_path}</small>
                                        </Link>
                                    )
                                    : <span key={index}>{link.label}<small>待补充 · {link.target_path}</small></span>;
                            })}
                        </div>
                    </section>
                )}

                {showAnalysis && canDiagnose && analysis.job_id && <AnalysisJobTrace jobId={analysis.job_id} />}
            </div>
        </main>
    );
}

export function MarketIntelligenceEntityPage() {
    const [params] = useSearchParams();
    const type = params.get("type");
    const entityKey = params.get("key")?.trim();
    const navigate = useNavigate();
    const { activeOrganization } = useAuth();
    const [entity, setEntity] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!type || !ENTITY_TYPES.has(type) || !entityKey) return undefined;
        let active = true;
        setEntity(null);
        setError(null);
        getEntity(type, entityKey)
            .then((value) => { if (active) setEntity(value); })
            .catch((cause) => { if (active) setError(errorMessage(cause, "实体读取失败")); });
        return () => { active = false; };
    }, [activeOrganization?.id, entityKey, type]);

    if (!type || !ENTITY_TYPES.has(type) || !entityKey) {
        return (
            <main className="mi-page">
                <div className="mi-error">
                    <h1>实体参数无效</h1>
                    <p>请从市场情报页面重新打开实体档案。</p>
                    <Link to="/market-intelligence">返回市场情报</Link>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="mi-page">
                <div className="mi-error">
                    <h1>实体档案无法打开</h1>
                    <p>{error}</p>
                    <Link to="/market-intelligence">返回市场情报</Link>
                </div>
            </main>
        );
    }

    if (!entity) {
        return <main className="mi-page"><div className="mi-content"><div className="mi-panel">正在读取实体档案...</div></div></main>;
    }

    const result = {
        items: entity.documents,
        pagination: { page: 1, page_size: entity.documents.length || 20, total: entity.documents.length },
        available_filters: { routes: [], airlines: [], aircraft: [], sources: [] },
    };

    return (
        <main className="mi-page mi-detail">
            <div className="mi-content">
                <Link className="mi-back" to="/market-intelligence"><ArrowLeftOutlined />返回市场情报</Link>
                <header className="mi-detail-head">
                    <div>
                        <span className="mi-eyebrow">Entity Profile · {type.toUpperCase()}</span>
                        <h1>{entity.key}</h1>
                        <p>汇总明确字段关联的已发布文档；AI 候选关系不会静默并入确定关联。</p>
                    </div>
                    <ApartmentOutlined />
                </header>

                {entity.profile?.markdown && (
                    <section className="mi-panel">
                        <MarkdownReader markdown={entity.profile.markdown} wikiLinks={entity.profile.wiki_links} />
                    </section>
                )}

                <div className="mi-results-head">
                    <div>
                        <span className="mi-eyebrow">关联阅读</span>
                        <h2>相关情报</h2>
                    </div>
                    <span>{entity.documents.length} 篇</span>
                </div>

                <DocumentResults result={result} onClear={() => navigate("/market-intelligence")} />
            </div>
        </main>
    );
}
